import React from 'react';
import { useNavigate } from 'react-router';
import PrimaryButton from '@components/PrimaryButton/PrimaryButton';
import { useTranslation } from 'react-i18next';

// Import images
import ThumbsUpImg from '@assets/images/ThumbsUpImg.png';
import KeyImg from '@assets/images/KeyImg.png';
import ClockImg from '@assets/images/ClockImg.png';
import {
  ButtonContainer,
  Container,
  ContentContainer,
  Description,
  HeaderContainer,
  Icon,
  LinkText,
  Title,
  CenteredContentContainer
} from './CompletedProcess.styles';

export type CompletedProcessType = 'recovery' | 'pin-change' | 'processing' | 'under-review' | 'processing-reviewed'

interface CompletedProcessProps {
  /**
   * Type of completed process to display
   */
  type: CompletedProcessType;
  /**
   * Optional uuid for recovery process
   */
  uuid?: string;
  /**
   * Optional transaction uuid for processing process
   */
  transactionUuid?: string;
  /**
   * Optional code for processing process
   */
  code?: number;
}

/**
 * CompletedProcess component that displays different success/processing states
 */
const CompletedProcess: React.FC<CompletedProcessProps> = ({
  type,
  uuid,
  transactionUuid,
  code
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Define content based on type
  const getContent = () => {
    switch (type) {
      case 'recovery':
        return {
          icon: ThumbsUpImg,
          title: t('completedProcess.recovery.title'),
          description: t('completedProcess.recovery.description'),
          buttonText: t('completedProcess.recovery.buttonText'),
        };
      case 'pin-change':
        return {
          icon: KeyImg,
          title: t('completedProcess.pinChange.title'),
          description: t('completedProcess.pinChange.description'),
          buttonText: t('completedProcess.pinChange.buttonText'),
        };
      case 'processing':
        return {
          icon: ClockImg,
          transactionUuid,
          uuid,
          code,
          title: t('completedProcess.processing.title'),
          description: t('completedProcess.processing.description'),
          buttonText: t('completedProcess.processing.buttonText'),
          linkText: t('completedProcess.processing.linkText'),
        };
      case 'processing-reviewed':
        return {
          icon: ClockImg,
          transactionUuid,
          uuid,
          code,
          title: t('completedProcess.processingReviewed.title'),
          description: t('completedProcess.processingReviewed.description'),
          buttonText: t('completedProcess.processingReviewed.buttonText'),
          linkText: t('completedProcess.processingReviewed.linkText'),
        };
      case 'under-review':
        return {
          icon: ClockImg,
          title: t('completedProcess.underReview.title'),
          description: t('completedProcess.underReview.description'),
          buttonText: t('completedProcess.underReview.buttonText'),
        };
      default:
        return {
          icon: ThumbsUpImg,
          title: t('completedProcess.default.title'),
          description: t('completedProcess.default.description'),
          buttonText: t('completedProcess.default.buttonText'),
        };
    }
  };

  const content = getContent();

  const handleButtonClick = () => {
    navigate('/home');
  };

  if (type === 'under-review') {
    return (
      <Container>

        <CenteredContentContainer>
          <Icon src={content.icon} alt="Status icon" />
          <Title>{content.title}</Title>
          <Description>{content.description}</Description>
        </CenteredContentContainer>

        <ButtonContainer>
          <PrimaryButton
            onClick={handleButtonClick}
            variant="primary"
          >
            {content.buttonText}
          </PrimaryButton>
        </ButtonContainer>
      </Container>
    )
  }

  return (
    <Container>
      <ContentContainer>
        <HeaderContainer>
          <Icon src={content.icon} alt="Status icon" />
          <Title>{content.title}</Title>
          <Description>{content.description}</Description>
        </HeaderContainer>

      </ContentContainer>

      <ButtonContainer>
        {(content.uuid || content.transactionUuid) && (
          <LinkText onClick={() => navigate('/transaction-detail', {
            state: {
              uuid: content.uuid,
              transaction_uuid: content.transactionUuid,
              code: content.code,
            }
          })}>
            {content.linkText}
          </LinkText>
        )}
        <PrimaryButton
          onClick={handleButtonClick}
          variant="primary"
        >
          {content.buttonText}
        </PrimaryButton>
      </ButtonContainer>
    </Container>
  );
};

export default CompletedProcess;
